// Cliente HTTP del portal del cliente. Token propio (distinto al de agentes).
const TOKEN_KEY = 'vetaDeskPortalToken';

export function getPortalToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setPortalToken(token: string) {
  try { localStorage.setItem(TOKEN_KEY, token); } catch { /* sin storage: dura la pestaña */ }
}
export function clearPortalToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* nada */ }
}

export class PortalError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message); }
}

let onLost: () => void = () => {};
export const setPortalLostHandler = (fn: () => void) => { onLost = fn; };

export async function papi<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getPortalToken();
  let res: Response;
  try {
    res = await fetch(`/api/portal${path}`, {
      ...init,
      headers: {
        ...(init.body instanceof Blob || init.body instanceof ArrayBuffer ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers
      }
    });
  } catch {
    throw new PortalError(0, 'NETWORK', 'No hay conexión con el servidor.');
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const code = String(body.code ?? 'ERROR');
    if (res.status === 401 && (code === 'SESSION_EXPIRED' || code === 'SESSION_REVOKED')) {
      clearPortalToken();
      onLost();
    }
    throw new PortalError(res.status, code, String(body.error ?? 'Algo salió mal.'));
  }
  return body as T;
}

export async function download(path: string, fileName: string) {
  const res = await fetch(`/api/portal${path}`, { headers: { Authorization: `Bearer ${getPortalToken() ?? ''}` } });
  if (!res.ok) throw new PortalError(res.status, 'DOWNLOAD', 'No se pudo descargar');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = fileName; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export interface PortalMe {
  id: string; name: string; email: string; role: 'requester' | 'approver'; locale: 'es' | 'en';
  organizationId: string; organizationName: string; tenantName: string; tenantSlug: string; attachmentsEnabled: boolean;
  organizationLogo?: string | null;
}

export interface PortalTicketRow {
  number: string; subject: string; type: 'INC' | 'SOL' | 'REQ' | 'PRY'; status: string; holdReason: string | null;
  requesterName: string; assigneeName: string | null; open: boolean; awaitingApproval: boolean;
  createdAt: string | null; updatedAt: string | null;
}

export interface PortalQuote {
  version: number; lines: { phaseName: string; discipline: 'functional' | 'abap'; hours: number }[] | null; subtotalHours: number; mgmtPct: number; mgmtHours: number; hours: number; billing: 'bag' | 'additional'; price: number | null; currency: string; notes: string;
  validUntil: string | null; expired: boolean; canDecide: boolean;
  decision: { outcome: 'approved' | 'rejected' | 'changes'; by: string; at: string | null; reason: string | null } | null;
}

export interface PortalDetail {
  ticket: PortalTicketRow & {
    description: string; organizationName: string; canConfirm: boolean; canReopen: boolean;
    resolution: { notes: string; at: string | null } | null;
  };
  quote: PortalQuote | null;
  /** Veta Change: etapa de la entrega y pruebas del usuario. */
  delivery?: {
    stage: 'development' | 'uat' | 'correction' | 'deployment' | 'closed';
    uat: {
      round: number; notes: string; items: { criterion: string; result: 'pending' | 'accepted' | 'rejected'; comment: string }[];
      sentAt: string | null; decidedAt: string | null; decidedBy: string | null; autoAccepted: boolean;
      outcome: 'accepted' | 'rejected' | null; autoAcceptAt: string | null; canDecide: boolean;
    } | null;
    production: { date: string; warrantyUntil: string } | null;
  } | null;
  messages: { id: string; mine: boolean; fromCustomer: boolean; author: string; body: string; attachmentIds: string[]; createdAt: string | null }[];
  attachments: { id: string; messageId: string | null; fileName: string; size: number; createdAt: string | null }[];
}
